import { useEffect } from "react";

/** The schema.org @type of a JSON-LD text, or "" when it cannot be read. Arrays and @graph use their first typed node. */
export function ldTypeKey(text: string): string {
  try {
    const parsed = JSON.parse(text);
    const node = Array.isArray(parsed) ? parsed[0] : parsed?.["@graph"]?.[0] ?? parsed;
    const t = node?.["@type"];
    return Array.isArray(t) ? t.join(",") : typeof t === "string" ? t : "";
  } catch {
    return "";
  }
}

/** Scripts created by live hook instances. Anything else of the same type (served HTML, or a block baked in by prerendering) is stale. */
const liveScripts = new Set<HTMLScriptElement>();

/** Remove every JSON-LD block of `key` in the head that no mounted hook owns. Returns how many were removed. */
export function removeStaleLd(root: ParentNode, key: string): number {
  let removed = 0;
  root.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]').forEach((el) => {
    if (liveScripts.has(el)) return;
    if (ldTypeKey(el.textContent || "") === key) {
      el.remove();
      removed++;
    }
  });
  return removed;
}

/**
 * Injects a JSON-LD <script> tag into the document head for the lifetime of
 * the calling component, removing it on unmount so it never persists onto
 * an unrelated page.
 *
 * The server already sends the same block in the HTML, and prerendered pages even carry blocks that an earlier client
 * run injected (they keep the data-client-ld attribute). To avoid the rendered DOM holding every block twice
 * (QA WF-04, B-0052), any block of the same @type that no live hook owns is replaced by ours instead of kept beside it.
 */
export function useStructuredData(schema: Record<string, unknown> | null) {
  useEffect(() => {
    if (!schema) return;

    const key = ldTypeKey(JSON.stringify(schema));
    if (key) removeStaleLd(document.head, key);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-client-ld", "1");
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    liveScripts.add(script);

    return () => {
      liveScripts.delete(script);
      script.remove();
    };
  }, [schema]);
}
